import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { CrudDetailState } from '../../interfaces/CrudDetailState'
import { Prefix, PrefixType } from '../../interfaces/Prefix'
import { getTempoQuote } from '../../../httpClient/Requests/Api/TEMPO/quote'
import { getTempoProject } from '../../../httpClient/Requests/Api/TEMPO/project'
import { getMemsourceProject } from '../../../httpClient/Requests/Api/MEMSOURCE/project'
import memsourceStatusTransform from '../../../ui/Specifics/MEMSOURCE/helpers/statusTransform'
import xtrfStatusTransform from '../../../ui/Specifics/XTRF/helpers/statusTransform'
import ProjectDetails from '../../../httpClient/interfaces/ProjectDetails'
import { getXtrfProject } from '../../../httpClient/Requests/Api/XTRF/project'
import { getXtrfQuote } from '../../../httpClient/Requests/Api/XTRF/quote'
import tempoStatusTransform from '../../../ui/Specifics/TEMPO/helpers/statusTransform'

export const initialState: CrudDetailState = {
    item: null,
    loading: false,
    error: null,
}

export const fetchCurrentProject = (prefix: PrefixType) =>
    createAsyncThunk(
        `${prefix}/currentProject/fetchCurrentProject`,
        async ({ id }: { id: string; executionCondition?: boolean }, { signal }) => {
            const source = axios.CancelToken.source()
            signal.addEventListener('abort', () => {
                source.cancel()
            })

            switch (prefix) {
                case Prefix.MEMSOURCE: {
                    const { data } = await getMemsourceProject({ uid: id })
                    const projectDetails: ProjectDetails = {
                        ...data,
                        status: memsourceStatusTransform(data.status),
                    }
                    return projectDetails
                }
                case Prefix.XTRF_PROJECTS: {
                    const { data } = await getXtrfProject({ projectId: id })
                    const projectDetails = {
                        ...data,
                        status: xtrfStatusTransform(data.status),
                    }
                    return projectDetails
                }
                case Prefix.XTRF_QUOTES: {
                    const { data } = await getXtrfQuote({ quoteId: id })
                    const projectDetails = {
                        ...data,
                        status: {
                            name: 'xtrf.page_project_list.status.quote_pending',
                            color: 'pending',
                        },
                    }
                    return projectDetails
                }
                case Prefix.TEMPO_QUOTES: {
                    const { data } = await getTempoQuote({ id }, source.token)
                    return data
                }
                case Prefix.TEMPO: {
                    const { data } = await getTempoProject({ projectId: id }, source.token)
                    const projectDetails = {
                        ...data,
                        status: tempoStatusTransform(data.status),
                    }
                    return projectDetails
                }
                default:
                    return null
            }
        },
        {
            condition: ({ executionCondition }) => {
                if (!executionCondition) {
                    // Already fetched or in progress, don't need to re-fetch
                    return false
                }
            },
        },
    )

const currentProjectSlice = (prefix: PrefixType) =>
    createSlice({
        name: `${prefix}/currentProject`,
        initialState,
        reducers: {
            resetCurrentProject: () => initialState,
        },
        extraReducers: (builder) => {
            builder.addCase(fetchCurrentProject(prefix).pending, (state) => ({ ...state, loading: true, error: null }))
            builder.addCase(fetchCurrentProject(prefix).fulfilled, (state, action) => ({
                ...state,
                item: action.payload,
                loading: false,
                error: null,
            }))
            builder.addCase(fetchCurrentProject(prefix).rejected, (state, action) => ({
                ...state,
                loading: false,
                error: action.error,
            }))
        },
    })

export const currentProjectActions = (prefix: PrefixType) => currentProjectSlice(prefix).actions

const currentProject = (prefix: PrefixType) => currentProjectSlice(prefix).reducer

export default currentProject
